import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import WizardFormFirstPage from "./SettingAccountIdentityVerification";
import WizardFormSecondPage from "./ManageUploadDocument";
import WizardFormThirdPage from "./UploadVideoForFaceVerify";
import WizardFormFourthPage from "./Questionnaire_Screen1";
import WizardFormFifthPage from "./Questionnaire_Screen2";
import WizardFormSixthPage from "./Questionnaire_Screen3";
import WizardFormSevenPage from "./Questionnaire_Screen4";
import WizardFormEightPage from "./Questionnaire_Screen5";
import WizardFormNinePage from "./Questionnaire_Screen6";
import WizardFormTenthPage from "./Questionnaire_Screen7";
import WizardFormElevenPage from "./RegistrationConfirmationForm";
import WizardFormTwelvePage from "./PhonenoVerification";
import WizardFormThirteenPage from "./DocumentProcessing";
import { RootState } from "../../store"; // Assuming the root state file path
import { validateKycToken } from "../../../Utils/user";
import { ToastBox, notifyError } from "MyComponents/Toaster";
import { reduxForm, InjectedFormProps } from "redux-form";
import useNetworkStatus from '../../../hooks/useNetworkStatus';
import { 
  getPersonalKycDraft, 
  updatePersonalKycDraftStep,
  resetPersonalKycDraft
} from "../../../Utils/personalKycDraft";
import { 
  setwizardPageNumber,
  setSelectedCountry,
  setSelectedDocument,
  setdriverLicenseFrontendPic,
  setdriverLicenseBackendPic,
  setidentityCardFrontendPic,
  setidentityCardBackendPic,
  setpassportFrontendPic,
  setpassportBackendPic,
  setotherIdentityFrontendPic,
  setotherIdentityBackendPic,
  setuserVideo,
  setfirstName,
  setlastName,
  setmiddleName,
  setdateOfBirth,
  setphoneNo,
  setstreetAddress,
  setcity,
  setpostalCode,
  setSelectedRegion,
  setdriverLicenseNumber,
  setidentityCardNumber,
  setpassportNumber,
  setotherIdentityNumber,
  setotherDocumentNumber,
  setQuestionnaireScreen1,
  setQuestionnaireScreen2,
  setQuestionnaireScreen3,
  setQuestionnaireScreen4,
  setQuestionnaireScreen5,
  setQuestionnaireScreen6,
  setKycToken
} from "../../authslice";
import OfflineBanner from "MyComponents/UI/OfflineBanner";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from "@mui/material";
import Header from "MyComponents/Includes/Header";
import Footer from "MyComponents/Includes/Footer";

const steps = [
  "Identity Verification",
  "Upload Document",
  "Face Verification",
  "Goals",
  "Source of Income",
  "Occupation",
  "Employment Status",
  "Cash Flows",
  "Poltical Background",
  "Ownership",
  "Confirmation",
  "Phone Number Verification",
  "Document Processing",
];

const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 15, // Centers the line under the 30px icon
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#e0e0e0',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: '#e0e0e0',
    borderLeftWidth: 2,
    borderRadius: 1,
    minHeight: '24px',
  },
}));

const CustomStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: ownerState.active || ownerState.completed ? '#00D084' : '#A9B5C1',
    zIndex: 1,
    color: '#fff',
    width: 30,
    height: 30,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.85rem',
    fontWeight: 'normal',
    ...(ownerState.error && {
      backgroundColor: '#f44336',
    }),
  }),
);

function CustomStepIcon(props: StepIconProps) {
  const { active, completed, error, className, icon } = props;

  return (
    <CustomStepIconRoot ownerState={{ completed, active, error }} className={className}>
      {completed ? '✓' : String(icon)}
    </CustomStepIconRoot>
  );
}

const WizardPersonalAccountKyc: React.FC<InjectedFormProps<FormData>> = (
  props
) => {
  const dispatch = useDispatch();
  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);

  // Draft/Dialog State
  const [showResumeDialog, setShowResumeDialog] = React.useState(false);
  const [showSubmittedDialog, setShowSubmittedDialog] = React.useState(false);
  const [fetchedDraft, setFetchedDraft] = React.useState<any>(null);

  // Stepper validation logic
  const stepValidation = useSelector(
    (state: RootState) => state.auth.wizardStepValidation
  );
  const isStepValid = (step: number) => stepValidation[step];

  const navigate = useNavigate();
  const { userId, kycToken } = useParams<{ userId: string; kycToken: string }>();
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    const validateUser = async () => {
      if (!userId || !kycToken) {
        navigate("/404");
        return;
      }

      try {
        const validationResult = await validateKycToken(userId, kycToken);
        if (!validationResult) {
          setTimeout(() => navigate("/404"), 2000);
          return;
        }

        dispatch(setKycToken(kycToken));

        const accountType = validationResult.accountType;

        if (accountType === "entity") {
          notifyError("Redirecting to entity KYC...");
          setTimeout(() => navigate(`/kyc/entity/${userId}/${kycToken}`), 2000);
        } else if (accountType === "personal") {
          // CHECK FOR DRAFT (Personal)
          const draft = await getPersonalKycDraft(userId);
          if (draft) {
            const { status, draftData } = draft;

            if (status === 'submitted') {
              setShowSubmittedDialog(true);
            } else if (draftData && (
                draftData.identity !== null || 
                (draftData.questionnaire && Object.keys(draftData.questionnaire).length > 0) || 
                (draftData.wizardState && draftData.wizardState.pageNumber > 1)
              )) {
              setFetchedDraft(draftData);
              setShowResumeDialog(true);
            } else {
              // New user or empty draft
              dispatch(setwizardPageNumber(1));
              localStorage.setItem('pageNumber', '1');
            }
          } else {
            // New user, start at Page 1
            dispatch(setwizardPageNumber(1));
            localStorage.setItem('pageNumber', '1');
          }
        } else {
          setTimeout(() => navigate("/404"), 1000);
        }
      } catch (error) {
        console.error("Error validating user:", error);
        setTimeout(() => navigate("/404"), 2000);
      }
    };

    validateUser();
  }, [userId, kycToken, navigate]);

  const handleNext = () => {
    if (isStepValid(page - 1)) {
      dispatch(setwizardPageNumber(page + 1)); // Move to next step
    }
  };

  const handleBack = () => {
    dispatch(setwizardPageNumber(page - 1)); // Go back to previous step
  };

  const handleResume = () => {
    if (fetchedDraft && fetchedDraft.draftData) {
      console.log("Resuming Personal Draft:", fetchedDraft);
      const data = fetchedDraft.draftData;

      // Hydrate Redux State from Draft
      if (data.identity) {
        if (data.identity.country) dispatch(setSelectedCountry(data.identity.country));
        if (data.identity.documentType) dispatch(setSelectedDocument(data.identity.documentType));
        if (data.identity.dob) dispatch(setdateOfBirth(data.identity.dob));
      }

      if (data.idVerification) {
        const idv = data.idVerification;
        if (idv.driverLicenseFrontendPic) dispatch(setdriverLicenseFrontendPic(idv.driverLicenseFrontendPic));
        if (idv.driverLicenseBackendPic) dispatch(setdriverLicenseBackendPic(idv.driverLicenseBackendPic));
        if (idv.identityCardFrontendPic) dispatch(setidentityCardFrontendPic(idv.identityCardFrontendPic));
        if (idv.identityCardBackendPic) dispatch(setidentityCardBackendPic(idv.identityCardBackendPic));
        if (idv.passportFrontendPic) dispatch(setpassportFrontendPic(idv.passportFrontendPic));
        if (idv.passportBackendPic) dispatch(setpassportBackendPic(idv.passportBackendPic));
        if (idv.otherIdentityFrontendPic) dispatch(setotherIdentityFrontendPic(idv.otherIdentityFrontendPic));
        if (idv.otherIdentityBackendPic) dispatch(setotherIdentityBackendPic(idv.otherIdentityBackendPic));
      }

      if (data.faceVerification) {
        if (data.faceVerification.userVideo) dispatch(setuserVideo(data.faceVerification.userVideo));
      }

      if (data.questionnaire) {
        const q = data.questionnaire;
        if (q.screen1) dispatch(setQuestionnaireScreen1(q.screen1));
        if (q.screen2) dispatch(setQuestionnaireScreen2(q.screen2));
        if (q.screen3) dispatch(setQuestionnaireScreen3(q.screen3));
        if (q.screen4) dispatch(setQuestionnaireScreen4(q.screen4));
        if (q.screen5) dispatch(setQuestionnaireScreen5(q.screen5));
        if (q.screen6) dispatch(setQuestionnaireScreen6(q.screen6));
      }

      if (data.confirmedDetails) {
        const cd = data.confirmedDetails;
        if (cd.firstName) dispatch(setfirstName(cd.firstName));
        if (cd.lastName) dispatch(setlastName(cd.lastName));
        if (cd.middleName) dispatch(setmiddleName(cd.middleName));
        if (cd.phoneno) dispatch(setphoneNo(cd.phoneno));
        if (cd.streetAddress) dispatch(setstreetAddress(cd.streetAddress));
        if (cd.city) dispatch(setcity(cd.city));
        if (cd.postalCode) dispatch(setpostalCode(cd.postalCode));
        if (cd.state) dispatch(setSelectedRegion(cd.state));
        
        // Map document number back
        if (cd.documentNumber) {
          const docType = data.identity?.documentType;
          if (docType === "Driving License") dispatch(setdriverLicenseNumber(cd.documentNumber));
          else if (docType === "Passport") dispatch(setpassportNumber(cd.documentNumber));
          else if (docType === "Identity Card") dispatch(setidentityCardNumber(cd.documentNumber));
          else if (docType === "Other Identity") dispatch(setotherIdentityNumber(cd.documentNumber));
          else dispatch(setotherDocumentNumber(cd.documentNumber));
        }
      }

      const resumePage = data.wizardState?.pageNumber || 1;
      dispatch(setwizardPageNumber(resumePage));
      localStorage.setItem('pageNumber', String(resumePage));
    }
    setShowResumeDialog(false);
  };

  const handleStartFresh = async () => {
    if (userId) {
      await resetPersonalKycDraft(userId);
    }
    dispatch(setwizardPageNumber(1));
    localStorage.setItem('pageNumber', '1');
    setShowResumeDialog(false);
  };

  const isStepFailed = (step: number) => {
    return !isStepValid(step);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header removed from here */}
      <OfflineBanner isOnline={isOnline} />
      <ToastBox />

      <Box sx={{ display: "flex", flex: 1, flexDirection: { xs: "column", md: "row" } }}>

        {/* STEPPER */}
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            width: "300px",
            paddingLeft: "3rem",
            marginTop: "6em",
            marginBottom: "3em",
            position: 'sticky',
            top: '6em',
            maxHeight: 'calc(100vh - 7em)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cfd8dc',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 4, color: '#0B1628' }}>
            KYC Verification
          </Typography>
          <Stepper
            activeStep={page - 1}
            orientation="vertical"
            connector={<CustomConnector />}
          >
            {steps.map((label, index) => {
              const labelProps = {
                error: isStepFailed(index + 1),
              };

              return (
                <Step key={label}>
                  <StepLabel
                    {...labelProps}
                    StepIconComponent={CustomStepIcon}
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '0.85rem',
                        color: '#A9B5C1',
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        color: '#00D084',
                        fontWeight: 500,
                      },
                      '& .MuiStepLabel-label.Mui-completed': {
                        color: '#707070',
                        fontWeight: 500,
                      },
                      '& .MuiStepLabel-label.Mui-error': {
                        color: '#f44336',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            flex: 1,
            paddingTop: { xs: "1.5em", md: "6em" },
            paddingLeft: { xs: "1.2rem", md: "2rem" },
            paddingRight: { xs: "1.2rem", md: "2rem" },
            marginBottom: "3em"
          }}
        >
          {/* Mobile Step Indicator */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1E344E' }}>
              KYC — Step {page} of {steps.length}: {steps[page - 1]}
            </Typography>
            <Box sx={{ width: '100%', height: '4px', backgroundColor: '#e0e0e0', mt: 1, borderRadius: '2px' }}>
              <Box sx={{ width: `${(page / steps.length) * 100}%`, height: '100%', backgroundColor: '#00D084', borderRadius: '2px' }} />
            </Box>
          </Box>

          <div className="wizard-content-wrapper">
            {page === 1 && <WizardFormFirstPage />}
            {page === 2 && <WizardFormSecondPage />}
            {page === 3 && <WizardFormThirdPage />}
            {page === 4 && <WizardFormFourthPage />}
            {page === 5 && <WizardFormFifthPage />}
            {page === 6 && <WizardFormSixthPage />}
            {page === 7 && <WizardFormSevenPage />}
            {page === 8 && <WizardFormEightPage />}
            {page === 9 && <WizardFormNinePage />}
            {page === 10 && <WizardFormTenthPage />}
            {page === 11 && <WizardFormElevenPage />}
            {page === 12 && <WizardFormTwelvePage />}
            {page === 13 && <WizardFormThirteenPage />}
          </div>
        </Box>

      </Box>

      <Footer full={true} />

      {/* Resume Application Dialog */}
      <Dialog
        open={showResumeDialog}
        onClose={() => setShowResumeDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            padding: '8px',
            maxWidth: '420px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(11,22,40,0.12)',
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2, px: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(0,208,132,0.10)',
              mb: 2,
            }}
          >
            <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 32, color: '#00D084' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B1628', mb: 1 }}>
            Resume Application?
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7A8D', lineHeight: 1.6 }}>
            We found a saved draft of your application. Would you like to pick up where you left off?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', gap: '10px', px: 4, pb: 4, pt: 1 }}>
          <MuiButton
            onClick={handleResume}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.3,
              fontSize: '0.95rem',
              backgroundColor: '#00D084',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#00b873', boxShadow: 'none' },
            }}
          >
            Resume Draft
          </MuiButton>
          <MuiButton
            onClick={handleStartFresh}
            fullWidth
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 500,
              py: 1.3,
              fontSize: '0.95rem',
              color: '#6B7A8D',
              border: '1.5px solid #E4ECF7',
              '&:hover': { backgroundColor: '#F5FAFF', borderColor: '#c8d6e5' },
            }}
          >
            Start Fresh
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Already Submitted Dialog */}
      <Dialog
        open={showSubmittedDialog}
        onClose={() => navigate("/personal_account_created")}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            padding: '8px',
            maxWidth: '420px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(11,22,40,0.12)',
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2, px: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: 'rgba(0,208,132,0.10)',
              mb: 2,
            }}
          >
            <i className="fa-solid fa-circle-check" style={{ fontSize: 32, color: '#00D084' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0B1628', mb: 1 }}>
            Already Submitted
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7A8D', lineHeight: 1.6 }}>
            You have already submitted your KYC application. Please check your dashboard for status updates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 4, pt: 1 }}>
          <MuiButton
            onClick={() => navigate("/personal_account_created")}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.3,
              fontSize: '0.95rem',
              backgroundColor: '#298DFE',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#1a7ae8', boxShadow: 'none' },
            }}
          >
            Go to Dashboard
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const WrappedWizardPersonalAccountKyc = reduxForm<FormData>({
  form: "kycwizard",
})(WizardPersonalAccountKyc);

export default WrappedWizardPersonalAccountKyc;
